import type { ListResponse, Where, SocketData } from '@juicyllama/llana-plugins-nuxtjs'
import type { ForumPostComment, ForumPostCommentsVote, ForumPostVote } from '~/types/forum'
import type { LlanaListOptions } from '~/types/llana'
import type { Profile } from '~/types/profile'

const table_name = 'forums_posts_comments'
type T = ForumPostComment
const default_sort = 'votes.desc'
const default_limit = 20
const default_relations = 'users,forums_posts_comments_upvotes,forums_posts_comments_downvotes'

export class CommentStore extends baseStore<T> {
	constructor(forum_id: number, post_id: number) {
		const store_name = 'forums_' + forum_id + '_posts_' + post_id + '_comments'
		super({
			table_name,
			store_name,
			default_sort,
			default_limit,
			default_relations,
		})
	}
}

export const useCommentStore = (forum_id: number, post_id: number) =>
	defineStore(`forums_${forum_id}_posts_${post_id}_comments`, {
		state: () => <ListResponse<T>>defaultList(),
		actions: {
			async bindEvents(where?: { [key: string]: string | number }) {
				const { $llanaSubscribe } = useNuxtApp()

				await $llanaSubscribe(
					table_name,
					(data: SocketData) => this.get(data.id as number, where),
					(data: SocketData) => this.get(data.id as number, where),
					(data: SocketData) => this.remove(data.id as number, where),
				)
			},
			async create(data: T) {
				const store = new CommentStore(forum_id, post_id)
				const { state, record } = await store.create(this.$state, data)
				this.$state = state
				return record
			},
			async get(id: number, where?: { [key: string]: string | number }): Promise<T | undefined> {
				const store = new CommentStore(forum_id, post_id)
				const { state, record } = await store.get(this.$state, id, where)
				this.$state = state
				return record
			},
			async list(options?: LlanaListOptions): Promise<ListResponse<T>> {
				const store = new CommentStore(forum_id, post_id)
				this.$state = await store.list(this.$state, options)
				return this.$state
			},
			async fetchMore(options?: {
				limit?: number
				offset?: number
				where?: Where[]
				relations?: string
				page?: string
				sort?: string
			}): Promise<ListResponse<T>> {
				const store = new CommentStore(forum_id, post_id)
				this.$state = await store.fetchMore(this.$state, options)
				return this.$state
			},
			async search(options?: {
				limit?: number
				offset?: number
				where?: Where[]
				relations?: string
				page?: string
				sort?: string
			}): Promise<ListResponse<T>> {
				const store = new CommentStore(forum_id, post_id)
				return await store.search(options)
			},
			async reload(): Promise<ListResponse<T>> {
				const store = new CommentStore(forum_id, post_id)
				this.$state = await store.reload()
				return this.$state
			},
			async update(id: number, data: Partial<T>): Promise<T> {
				const store = new CommentStore(forum_id, post_id)
				const { state, record } = await store.update(this.$state, id, data)
				this.$state = state
				return record
			},
			async remove(id: number, where?: { [key: string]: string | number }): Promise<void> {
				const store = new CommentStore(forum_id, post_id)
				this.$state = await store.remove(this.$state, id, where)
			},
			async delete(id: number): Promise<void> {
				const store = new CommentStore(forum_id, post_id)
				this.$state = await store.delete(this.$state, id, true)
			},
			/**
			 * STORE SPECIFIC ACTIONS
			 * * Upvote a comment
			 * * Remove an upvote
			 * * Downvote a comment
			 * * Remove a downvote
			 *
			 **/

			async upvote(vote: ForumPostVote, comment: ForumPostComment): Promise<void> {
				const { $llana } = useNuxtApp()
				const upvote = await $llana<ForumPostVote>({
					type: 'CREATE',
					table: 'forums_posts_comments_upvotes',
					data: vote,
				})

				const commentIndex = this.$state.data.findIndex((c: ForumPostComment) => c.id === comment.id)
				this.$state.data[commentIndex].forums_posts_comments_upvotes.push(upvote as ForumPostVote)
				this.$state.data[commentIndex].votes = this.$state.data[commentIndex].votes + 1
			},

			async removeUpvote(comment: ForumPostComment, profile: Profile): Promise<void> {
				const commentIndex = this.$state.data.findIndex((c: ForumPostComment) => c.id === comment.id)
				const voteIndex = this.$state.data[commentIndex].forums_posts_comments_upvotes.findIndex(
					(c: ForumPostCommentsVote) => c.user_id === profile.id,
				)

				if (voteIndex > -1) {
					const { $llana } = useNuxtApp()
					await $llana<ForumPostVote>({
						type: 'DELETE',
						table: 'forums_posts_comments_upvotes',
						id: this.$state.data[commentIndex].forums_posts_comments_upvotes[voteIndex].id,
						hard: true,
					})

					this.$state.data[commentIndex].forums_posts_comments_upvotes.splice(voteIndex, 1)
					this.$state.data[commentIndex].votes = this.$state.data[commentIndex].votes - 1
				}
			},

			async downvote(vote: ForumPostVote, comment: ForumPostComment): Promise<void> {
				const { $llana } = useNuxtApp()
				const upvote = await $llana<ForumPostVote>({
					type: 'CREATE',
					table: 'forums_posts_comments_downvotes',
					data: vote,
				})

				const commentIndex = this.$state.data.findIndex((c: ForumPostComment) => c.id === comment.id)
				this.$state.data[commentIndex].forums_posts_comments_downvotes.push(upvote as ForumPostVote)
				this.$state.data[commentIndex].votes = this.$state.data[commentIndex].votes - 1
			},

			async removeDownvote(comment: ForumPostComment, profile: Profile): Promise<void> {
				const commentIndex = this.$state.data.findIndex((c: ForumPostComment) => c.id === comment.id)
				const voteIndex = this.$state.data[commentIndex].forums_posts_comments_downvotes.findIndex(
					(c: ForumPostCommentsVote) => c.user_id === profile.id,
				)

				if (voteIndex > -1) {
					const { $llana } = useNuxtApp()
					await $llana<ForumPostVote>({
						type: 'DELETE',
						table: 'forums_posts_comments_downvotes',
						id: this.$state.data[commentIndex].forums_posts_comments_downvotes[voteIndex].id,
						hard: true,
					})

					this.$state.data[commentIndex].forums_posts_comments_downvotes.splice(voteIndex, 1)
					this.$state.data[commentIndex].votes = this.$state.data[commentIndex].votes + 1
				}
			},
		},
	})
